/**
 * Public record detail page.
 * Shows full bibliographic details and item availability.
 */

import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { recordTypeLabel } from "@/lib/recordTypes";
import { BookCover, ReservePanel } from "../components";
import { OnlineAccessCard, type OnlineAccess } from "../digital";

interface RecordDetail {
  id: string;
  type: string;
  title: string;
  subtitle: string | null;
  uniformTitle: string | null;
  parallelTitle: string | null;
  edition: string | null;
  isbn: string | null;
  issn: string | null;
  language: string;
  originalLanguage: string | null;
  publisher: string | null;
  publicationPlace: string | null;
  publicationDate: string | null;
  publicationYear: string | null;
  physicalDescription: string | null;
  dimensions: string | null;
  series: string | null;
  seriesNumber: string | null;
  classification: string | null;
  subjects: string[];
  summary: string | null;
  tableOfContents: string | null;
  notes: string | null;
  targetAudience: string | null;
  coverUrl: string | null;
  contributions: Array<{
    role: string;
    authority: {
      id: string;
      name: string;
      type: string;
    };
  }>;
  /** Creators of a title harvested from a digital resource (no authority records) */
  creators: string[];
  /** Platform giving online access to the title, when open to patrons */
  onlineAccess: OnlineAccess | null;
  availability: {
    availableCount: number;
    totalCount: number;
  };
}

interface AvailabilityData {
  recordId: string;
  locations: Array<{
    location: {
      id: string;
      name: string;
      type: string;
      address: string | null;
      city: string | null;
    };
    items: Array<{
      id: string;
      barcode: string;
      callNumber: string;
      status: string;
      isAvailable: boolean;
      shelfLocation: string | null;
      dueDate: string | null;
    }>;
    availableCount: number;
    totalCount: number;
  }>;
  totalAvailable: number;
  totalItems: number;
}

const ROLE_LABELS: Record<string, string> = {
  author: "Auteur",
  translator: "Traducteur",
  illustrator: "Illustrateur",
  editor: "Directeur de publication",
  contributor: "Contributeur",
  narrator: "Narrateur",
  composer: "Compositeur",
  performer: "Interprète",
  director: "Réalisateur",
  photographer: "Photographe",
};

const LANGUAGE_LABELS: Record<string, string> = {
  fre: "Français",
  eng: "Anglais",
  ger: "Allemand",
  spa: "Espagnol",
  ita: "Italien",
  ara: "Arabe",
  mul: "Multilingue",
};

const STATUS_LABELS: Record<string, string> = {
  available: "Disponible",
  on_loan: "Emprunté",
  reserved: "Réservé",
  in_transit: "En transit",
  in_processing: "En traitement",
  lost: "Perdu",
  withdrawn: "Retiré",
};

export function RecordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch record details
  const {
    data: recordData,
    isLoading,
    error,
  } = useQuery<{ record: RecordDetail }>({
    queryKey: ["portal", "record", id],
    queryFn: async (): Promise<{ record: RecordDetail }> => {
      const response = await fetch(`/api/portal/catalog/records/${id ?? ""}`);
      if (!response.ok) throw new Error("Record not found");
      return (await response.json()) as { record: RecordDetail };
    },
    enabled: !!id,
  });

  // Fetch availability
  const { data: availabilityData } = useQuery<AvailabilityData>({
    queryKey: ["portal", "record", id, "availability"],
    queryFn: async (): Promise<AvailabilityData> => {
      const response = await fetch(`/api/portal/catalog/records/${id ?? ""}/availability`);
      if (!response.ok) throw new Error("Failed to fetch availability");
      return (await response.json()) as AvailabilityData;
    },
    enabled: !!id,
  });

  const record = recordData?.record;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">Notice non trouvée.</p>
        <button
          onClick={() => {
            void navigate(-1);
          }}
          className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          ← Retour
        </button>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/opac" className="hover:text-blue-600 dark:hover:text-blue-400">
          Accueil
        </Link>
        <span>/</span>
        <Link to="/opac/search" className="hover:text-blue-600 dark:hover:text-blue-400">
          Catalogue
        </Link>
        <span>/</span>
        <span className="truncate text-gray-900 dark:text-white">{record.title}</span>
      </nav>

      {/* Main content */}
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Left column - Cover and availability */}
        <div className="space-y-6 lg:col-span-1">
          {/* Cover */}
          <BookCover
            coverUrl={record.coverUrl}
            alt={`Couverture de ${record.title}`}
            type={record.type}
            size="lg"
            className="rounded-xl"
          />

          {record.onlineAccess && (
            <OnlineAccessCard recordId={record.id} access={record.onlineAccess} />
          )}

          {/* Availability summary (a title only available online has no copies) */}
          {(!record.onlineAccess || record.availability.totalCount > 0) && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Disponibilité</h3>
              <div
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${
                  record.availability.availableCount > 0
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                }`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    record.availability.availableCount > 0 ? "bg-green-500" : "bg-yellow-500"
                  }`}
                />
                {record.availability.availableCount > 0
                  ? `${String(record.availability.availableCount)} disponible${record.availability.availableCount > 1 ? "s" : ""}`
                  : "Indisponible"}
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {String(record.availability.totalCount)} exemplaire
                {record.availability.totalCount > 1 ? "s" : ""} au total
              </p>

              {/* Reservation: a copy on the shelf is set aside, otherwise the patron joins the queue */}
              {record.availability.totalCount > 0 && <ReservePanel recordId={record.id} />}
            </div>
          )}
        </div>

        {/* Right column - Details */}
        <div className="space-y-6 lg:col-span-2">
          {/* Title and main info */}
          <div>
            <div className="flex items-start gap-3">
              <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {recordTypeLabel(record.type)}
              </span>
            </div>
            <h1 className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">
              {record.title}
            </h1>
            {record.subtitle && (
              <p className="mt-2 text-xl text-gray-600 dark:text-gray-400">{record.subtitle}</p>
            )}

            {/* Creators of a harvested digital title */}
            {record.contributions.length === 0 && record.creators.length > 0 && (
              <p className="mt-4 font-medium text-gray-700 dark:text-gray-300">
                {record.creators.join(", ")}
              </p>
            )}

            {/* Authors */}
            {record.contributions.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {record.contributions.map((contrib, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300"
                  >
                    <span className="font-medium">{contrib.authority.name}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({ROLE_LABELS[contrib.role] || contrib.role})
                    </span>
                    {index < record.contributions.length - 1 && (
                      <span className="mx-1 text-gray-300 dark:text-gray-600">•</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          {record.summary && (
            <div>
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">Résumé</h2>
              <p className="leading-relaxed text-gray-700 dark:text-gray-300">{record.summary}</p>
            </div>
          )}

          {/* Details table */}
          <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <h2 className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-900 dark:border-gray-700 dark:text-white">
              Détails
            </h2>
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
              {record.publisher && <DetailRow label="Éditeur" value={record.publisher} />}
              {record.publicationPlace && (
                <DetailRow label="Lieu de publication" value={record.publicationPlace} />
              )}
              {record.publicationYear && <DetailRow label="Année" value={record.publicationYear} />}
              {record.edition && <DetailRow label="Édition" value={record.edition} />}
              {record.isbn && <DetailRow label="ISBN" value={record.isbn} />}
              {record.issn && <DetailRow label="ISSN" value={record.issn} />}
              {record.language && (
                <DetailRow
                  label="Langue"
                  value={LANGUAGE_LABELS[record.language] || record.language}
                />
              )}
              {record.originalLanguage && (
                <DetailRow
                  label="Langue originale"
                  value={LANGUAGE_LABELS[record.originalLanguage] || record.originalLanguage}
                />
              )}
              {record.physicalDescription && (
                <DetailRow label="Description" value={record.physicalDescription} />
              )}
              {record.dimensions && <DetailRow label="Dimensions" value={record.dimensions} />}
              {record.series && (
                <DetailRow
                  label="Collection"
                  value={
                    record.seriesNumber ? `${record.series}, ${record.seriesNumber}` : record.series
                  }
                />
              )}
              {record.classification && (
                <DetailRow label="Classification" value={record.classification} />
              )}
              {record.targetAudience && <DetailRow label="Public" value={record.targetAudience} />}
            </dl>
          </div>

          {/* Subjects */}
          {record.subjects.length > 0 && (
            <div>
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">Sujets</h2>
              <div className="flex flex-wrap gap-2">
                {record.subjects.map((subject, index) => (
                  <Link
                    key={index}
                    to={`/opac/search?q=${encodeURIComponent(subject)}`}
                    className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
                  >
                    {subject}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Items by location */}
          {availabilityData && availabilityData.locations.length > 0 && (
            <div>
              <h2 className="mb-4 font-semibold text-gray-900 dark:text-white">
                Où trouver ce document
              </h2>
              <div className="space-y-4">
                {availabilityData.locations.map((loc) => (
                  <div
                    key={loc.location.id}
                    className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {loc.location.name}
                        </h3>
                        {loc.location.address && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {loc.location.address}
                            {loc.location.city && `, ${loc.location.city}`}
                          </p>
                        )}
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          loc.availableCount > 0
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                        }`}
                      >
                        {loc.availableCount}/{loc.totalCount} disponible
                        {loc.availableCount > 1 ? "s" : ""}
                      </span>
                    </div>
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {loc.items.map((item) => (
                        <li key={item.id} className="flex items-center justify-between px-4 py-3">
                          <div>
                            <span className="font-mono text-sm text-gray-900 dark:text-white">
                              {item.callNumber}
                            </span>
                            {item.shelfLocation && (
                              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                ({item.shelfLocation})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {item.dueDate && (
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Retour prévu : {formatDate(item.dueDate)}
                              </span>
                            )}
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                                item.isAvailable
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                              }`}
                            >
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${
                                  item.isAvailable ? "bg-green-500" : "bg-yellow-500"
                                }`}
                              />
                              {STATUS_LABELS[item.status] || item.status}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex px-4 py-3">
      <dt className="w-1/3 shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-white">{value}</dd>
    </div>
  );
}

export default RecordDetailPage;
